import type { OrderResponse } from "@/types/external"

export type OrderFeeRow = { key: string; label: string; value: number }

type OrderFee = NonNullable<OrderResponse["otherFees"]>[number]

const FEE_TYPE_LABELS: Record<string, string> = {
  SERVICE_FEE: "Taxa de serviço",
  DELIVERY_FEE: "Taxa de entrega",
  TIP: "Gorjeta",
}

/**
 * PT-BR label of a fee, by its Open Delivery type. `observation` is the
 * human-readable copy the order carries; `name` is a machine code, never shown.
 */
export function feeLabel(fee: OrderFee): string {
  return FEE_TYPE_LABELS[fee.type] || fee.observation || "Taxa"
}

/**
 * The fee lines of the order summary, one row per fee in `otherFees[]` so a Pix
 * service fee is never folded into the delivery fee. A non-INDOOR order always
 * gets a delivery row (value 0 renders as "Grátis"), and when `otherFees[]` is
 * missing altogether the aggregate `total.otherFees` stands in, so the customer
 * never sees a subtotal + fees that don't add up to the total.
 */
export function orderFeeRows(order: OrderResponse): OrderFeeRow[] {
  const fees = order.otherFees ?? []
  const isIndoor = order.type === "INDOOR"

  if (fees.length === 0) {
    const aggregate = order.total?.otherFees?.value ?? 0
    if (isIndoor) {
      return aggregate > 0 ? [{ key: "total-fees", label: "Taxas", value: aggregate }] : []
    }
    return [{ key: "total-fees", label: "Taxa de entrega", value: aggregate }]
  }

  const rows = fees
    .map((fee, index) => ({
      key: `${fee.type || "fee"}-${index}`,
      label: feeLabel(fee),
      value: fee.price?.value ?? 0,
      type: fee.type,
    }))
    .filter((row) => row.value > 0 || row.type === "DELIVERY_FEE")
    .filter((row) => !(isIndoor && row.type === "DELIVERY_FEE"))

  if (!isIndoor && !rows.some((row) => row.type === "DELIVERY_FEE")) {
    rows.push({ key: "delivery-free", label: "Taxa de entrega", value: 0, type: "DELIVERY_FEE" })
  }

  return rows.map(({ key, label, value }) => ({ key, label, value }))
}
